import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  DataGrid,
  IconButton,
  Loading,
  Tooltip,
  Typography,
} from "../../components";
import { PermisoesHelper } from "../../helpers";
import {
  ConfiguracoesAgendaFuncionamentoService,
  notification,
  PessoaService,
  ServicoRecorrenteService,
  SessionService,
} from "../../services";
import { formatDate, responseErros } from "../../utils";
import {
  calculaDiferencaDias,
  getAcoesButtonInfo,
  getStatusInfo,
  renderColorAviso,
  renderTextoAviso,
} from "./painel-servicos-recorrentes";
import { useImportContext } from "../../contexts/import.context";
import { AgendamentoServicoDialog, EnvioWhatsappDialog, FiltroCustom } from "./components";

const servicosRecorrentesService = new ServicoRecorrenteService();
const pessoaService = new PessoaService();
const sessionService = new SessionService();
const agendaFuncionamentoService =
  new ConfiguracoesAgendaFuncionamentoService();

const PainelServicosRecorrentes = () => {
  const [openDialogEnvioWhatsapp, setOpenDialogEnvioWhatsapp] = useState(false);
  const [openDialogAgendamento, setOpenDialogAgendamento] = useState(false);
  const [loading, setLoading] = useState(false);
  const [servicosRecorrentes, setServicosRecorrentes] = useState([]);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [horariosFuncionamento, setHorariosFuncionamento] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("ordem-servicos-visualizar");
  const dadosEmpresa = sessionService.getEmpresaAndUsuario();
  const permissaoWhatsappSimples = dadosEmpresa.empresa.whatsappSimples;
  const permissaoWhatsappAvancado = dadosEmpresa.empresa.whatsappAvancado;
  const { initialCollapses } = useImportContext();

  const colunaAcoes = permissaoWhatsappSimples
    ? [
        {
          field: "acoes",
          headerName: "Ações",
          sortable: false,
          width: 150,
          align: "center",
          headerAlign: "center",
          disableClickEventBubbling: true,
          renderCell: (params) => {
            const status = params.row.status,
              dataVencimento = params.row.dataVencimento;
            const diferencaDias = calculaDiferencaDias(dataVencimento);
            const requerAcao =
              (status === "PENDENTE" && diferencaDias <= 30) ||
              status === "NOTIFICADO";
            const { label, icon, backgroundColor } = getAcoesButtonInfo(status);

            return (
              requerAcao && (
                <Button
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={() => handleActions(params.row)}
                  sx={{
                    textTransform: "none",
                    padding: "6px 14px",
                    backgroundColor: "#fdf9fa",
                    border: "1px solid #d3d3d3",
                    borderRadius: "10px",
                    "&:hover": {
                      backgroundColor: `${backgroundColor}c7`,
                      border: `1px solid ${backgroundColor}33`,
                      "& .ph-fill": {
                        color: "#fcfcfc !important",
                      },
                      "& .MuiTypography-root": {
                        color: "#fcfcfc",
                      },
                    },
                  }}
                >
                  <i
                    className={`ph-fill ${icon}`}
                    style={{
                      fontSize: 18,
                      marginRight: 7,
                      color: "#001718",
                    }}
                  ></i>
                  <Typography
                    sx={{
                      fontSize: 13,
                      marginTop: "1px",
                      fontWeight: "500",
                      color: "#001718",
                      fontFamily: "Montserrat",
                    }}
                  >
                    {label}
                  </Typography>
                </Button>
              )
            );
          },
        },
      ]
    : [];

  const colunas = [
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 300,
      sortable: false,
    },
    {
      field: "chaveAtivo",
      headerName: "Ativo",
      flex: 150,
      sortable: false,
    },
    {
      field: "servico",
      headerName: "Serviço",
      flex: 300,
      sortable: false,
      renderCell: (params) => {
        return (
          <div className="d-flex flex-column">
            <span className="fw-bold">{params.value}</span>
            <span
              style={{ fontSize: "13px", color: "#51686f" }}
            >{`a cada ${params.row.prazoRecorrencia} dias`}</span>
          </div>
        );
      },
    },
    {
      field: "numeroDocumento",
      headerName: "OS",
      flex: 120,
      sortable: false,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>{`${params.row.serieDocumento}-${params.value}`}</span>
          <Tooltip title="Abrir Ordem de Serviço" arrow placement="top">
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                abrirOrdemServico(params?.row.ordemServicoId);
              }}
              size="small"
            >
              <i
                className="ph-fill ph-arrow-square-out"
                style={{ fontSize: 20 }}
              ></i>
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      field: "dataVencimento",
      headerName: "Vencimento",
      flex: 150,
      sortable: true,
      renderCell: (params) => {
        const diferencaDias = calculaDiferencaDias(params.value);
        const texto = renderTextoAviso(diferencaDias);
        const color = renderColorAviso(diferencaDias);

        return (
          <div className="d-flex flex-column">
            <span>
              {format(formatDate.received(params.value), "dd/MM/yyyy")}
            </span>
            <span style={{ fontSize: "13px", color }}>{texto}</span>
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      align: "center",
      headerAlign: "center",
      width: 140,
      renderCell: (params) => {
        const { label, backgroundColor, color } = getStatusInfo(
          params.value,
          params.row.dataVencimento,
        );
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: `${backgroundColor}DD`,
              borderRadius: "4px",
              height: "23px",
              width: "100%",
              "& .MuiChip-label": {
                fontWeight: "600",
                fontSize: "11px",
                letterSpacing: ".2px",
                color,
              },
            }}
          />
        );
      },
    },
    ...colunaAcoes,
  ];

  const abrirOrdemServico = (id) => {
    const novaUrl = window.location.pathname.replace(
      /painel-servicos-recorrentes$/,
      `ordem-servicos/${id}`,
    );
    window.open(novaUrl, "_blank");
  };

  const buscaPessoa = async (id) => {
    const result = await pessoaService.getById(id);
    if (!result.isAxiosError) {
      return result.data;
    } else {
      responseErros(result);
      return null;
    }
  };

  async function buscarHorariosFuncionamento() {
    const res = await agendaFuncionamentoService.find();
    if (!res.isAxiosError) {
      setHorariosFuncionamento(res.data);
    } else {
      responseErros(res);
    }
  }

  const handleActions = async (servico) => {
    const cliente = await buscaPessoa(servico?.clienteId);
    setServicoSelecionado({
      ...servico,
      celular: cliente?.celular,
    });

    if (servico.status === "NOTIFICADO") {
      await buscarHorariosFuncionamento();
      setOpenDialogAgendamento(true);
    } else {
      setOpenDialogEnvioWhatsapp(true);
    }
  };

  useEffect(() => {
    buscarServicosRecorrentes();
  }, []);

  const buscarServicosRecorrentes = async (filtros) => {
    setLoading(true);
    const result = await servicosRecorrentesService.getAll(filtros);
    if (!result.isAxiosError) {
      setServicosRecorrentes(result.data.rows);
      setRowCount(result.data.count);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const atualizarStatus = async (id, status) => {
    const result = await servicosRecorrentesService.atualizarStatus(id, {
      status,
    });
    if (!result.isAxiosError) {
      await buscarServicosRecorrentes();
    } else {
      responseErros(result);
    }
  };

  const enviarServicosPendentes = async () => {
    setLoading(true);
    const result = await servicosRecorrentesService.enviarMensagemLote({
      baseUrl: window.location.origin,
      diasSemanaFuncionamento: dadosEmpresa.empresa.configuracoes.diasSemanaFuncionamento,
      intervaloAgendamentos: dadosEmpresa.empresa.configuracoes.intervaloAgendamentos,
    });
    if (!result.isAxiosError) {
      notification.sucessoGenericos("Requisição enviada com sucesso!");
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      buscarServicosRecorrentes(props.filtros);
    }
  };

  return (
    <div className="list-layout-container">
      <Card>
        <CardHeader title="Painel de Serviços Recorrentes" botaoVoltar />
        <CardContent>
          <DataGrid
            rows={servicosRecorrentes}
            rowCount={rowCount}
            columns={colunas}
            paginationMode={"server"}
            visualizarShow={permissoesHelper.temPermisao(
              "ordem-servicos-visualizar",
            )}
            sendServer={(props) => sendServerDatagrid(props)}
            disableAcoes
            enableRowDoubleClick
            onClickEdit={(id) => {
              const servico = servicosRecorrentes.find(
                (item) => id === item.id,
              );
              abrirOrdemServico(servico.ordemServicoId);
            }}
            hideFiltroAvancado
            FiltroCustom={
              <FiltroCustom
                sendServer={sendServerDatagrid}
                enviarServicosPendentes={enviarServicosPendentes}
                permissaoWhatsappAvancado={permissaoWhatsappAvancado}
              />
            }
          />
        </CardContent>
      </Card>
      <Loading loading={loading} />
      <EnvioWhatsappDialog
        open={openDialogEnvioWhatsapp}
        setOpen={setOpenDialogEnvioWhatsapp}
        data={{
          ...servicoSelecionado,
          ativo: servicoSelecionado?.chaveAtivo,
          nomeServico: servicoSelecionado?.servico,
          apelidoFantasia: dadosEmpresa?.empresa?.fantasia,
          nomeUsuario: dadosEmpresa?.usuario?.nome,
        }}
        dadosEmpresa={dadosEmpresa}
        initialCollapses={initialCollapses}
        atualizarStatus={atualizarStatus}
      />
      <AgendamentoServicoDialog
        open={openDialogAgendamento}
        setOpen={setOpenDialogAgendamento}
        data={servicoSelecionado}
        atualizarStatus={atualizarStatus}
        setLoading={setLoading}
        horariosFuncionamento={horariosFuncionamento}
      />
    </div>
  );
};

export default PainelServicosRecorrentes;
